import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Printer, FileText, ShieldCheck, Ban, Fuel, Truck } from 'lucide-react';
import { PageShell, primaryButton, ghostButton, dangerButton, inputStyle, glassLight, T, Badge, StatCard, EmptyState, Field, SectionTitle, usePagination, Pagination, useIsMobile } from '../ui';
import { LOGO_DATA_URI } from '../Logo';
import type { Pedido, PedidoLinea, ProductoWOP } from '../types';

// Mismo truco que en Contratos.tsx: escribir el tag de apertura de "style"
// literal dentro de un template literal hace que el formateador de código lo
// interprete como JSX y corrompa el archivo. Se arma por concatenación.
const LT = '<';
const styleTag = (css: string) => `${LT}style>${css}${LT}/style>`;

// ── DATOS DEL EMISOR ────────────────────────────────────────────────────────
// Encabezado del formulario oficial: la empresa que COLOCA el pedido.
// >>> COMPLETAR con el RNC, dirección y teléfono reales de la empresa. <<<
// Los campos vacíos se imprimen como línea en blanco para llenar a mano.
const EMPRESA = {
  nombre: 'JUNQUITO GAS, S.R.L.',
  rnc: '',
  direccion: '',
  telefono: ''
};

// Destinatario fijo del formulario (el suplidor bajo contrato).
const DESTINATARIO = 'Refinería Dominicana de Petróleos, S.A.';

// ── PRODUCTOS ───────────────────────────────────────────────────────────────
// Columnas del formato WOP, en el mismo orden que el formulario impreso.
const PRODUCTOS_WOP: {
  id: ProductoWOP;
  label: string;
}[] = [{
  id: 'jetA1',
  label: 'Jet A-1 / Kerosen'
}, {
  id: 'gasolinaRegular',
  label: 'Gasolina Regular'
}, {
  id: 'gasolinaPremium',
  label: 'Gasolina Premium'
}, {
  id: 'gasoilRegular',
  label: 'Gasoil Regular'
}, {
  id: 'gasoilOptimo',
  label: 'Gasoil Óptimo'
}, {
  id: 'fuelOilReg',
  label: 'Fuel Oil Reg.'
}, {
  id: 'fuelOilPrem',
  label: 'Fuel Oil Prem.'
}];
const COMPARTIMIENTOS = [1, 2, 3, 4, 5, 6];
const fmtGal = (n: number) => new Intl.NumberFormat('es-DO').format(n || 0);
const fmtFecha = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};
const hoyISO = () => new Date().toISOString().split('T')[0];
const totalGalones = (p: Pedido) => p.lineas.reduce((sum, l) => sum + (l.galones || 0), 0);
const estadoStyle = (estado: Pedido['estado']) => {
  if (estado === 'autorizado') return {
    color: T.ok,
    soft: T.okSoft,
    label: 'Autorizado'
  };
  if (estado === 'anulado') return {
    color: T.danger,
    soft: T.dangerSoft,
    label: 'Anulado'
  };
  return {
    color: T.warn,
    soft: T.warnSoft,
    label: 'Borrador'
  };
};
const esc = (s: string) => (s ?? '').toString().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Abre una ventana nueva con el documento ya armado y lanza la impresión.
function openPrintWindow(html: string) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('El navegador bloqueó la ventana. Habilita las ventanas emergentes.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};

// Línea vacía para un compartimiento que todavía no se llenó.
const lineaVacia = (compartimiento: number): PedidoLinea => ({
  compartimiento,
  producto: 'gasoilRegular',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const lineaGLPVacia = (): PedidoLinea => ({
  compartimiento: 1,
  producto: 'glp',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const pedidoVacio = (): Partial<Pedido> => ({
  tipo: 'GLP',
  numeroOrden: '',
  fecha: hoyISO(),
  conductor: '',
  paseChofer: '',
  fichaCola: '',
  placaCamion: '',
  lineas: [lineaGLPVacia()],
  estado: 'borrador',
  observaciones: ''
});
interface PedidosProps {
  pedidos: Pedido[];
  onAdd: (p: Pedido) => void;
  onUpdate: (p: Pedido) => void;
  onDelete: (id: string) => void;
  // Permisos resueltos por el rol del usuario (ver App.tsx).
  canEdit: boolean;
  canArchive: boolean;
  canAutorizar: boolean;
  // Quién firma cuando se autoriza una orden.
  usuarioNombre: string;
  usuarioCargo: string;
  onLog?: (mensaje: string) => void;
}
export function Pedidos({
  pedidos,
  onAdd,
  onUpdate,
  onDelete,
  canEdit,
  canArchive,
  canAutorizar,
  usuarioNombre,
  usuarioCargo,
  onLog
}: PedidosProps) {
  const isMobile = useIsMobile();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<'todos' | 'WOP' | 'GLP'>('todos');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'borrador' | 'autorizado' | 'anulado'>('todos');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Pedido>>(pedidoVacio());
  const lineas = form.lineas ?? [];

  // ── FILTRADO ──────────────────────────────────────────────────────────
  const filtrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return pedidos.filter(p => filterTipo === 'todos' || p.tipo === filterTipo).filter(p => filterEstado === 'todos' || p.estado === filterEstado).filter(p => {
      if (!q) return true;
      const enDestinos = p.lineas.some(l => l.destino.toLowerCase().includes(q) || l.rncDestino.toLowerCase().includes(q));
      return p.numeroOrden.toLowerCase().includes(q) || p.conductor.toLowerCase().includes(q) || p.placaCamion.toLowerCase().includes(q) || enDestinos;
    }).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [pedidos, searchTerm, filterTipo, filterEstado]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados, 6);

  // ── ESTADÍSTICAS ──────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const vivos = pedidos.filter(p => p.estado !== 'anulado');
    const mes = hoyISO().slice(0, 7);
    return {
      total: pedidos.length,
      borradores: pedidos.filter(p => p.estado === 'borrador').length,
      autorizados: pedidos.filter(p => p.estado === 'autorizado').length,
      galonesMes: vivos.filter(p => (p.fecha || '').startsWith(mes)).reduce((s, p) => s + totalGalones(p), 0)
    };
  }, [pedidos]);

  // ── FORMULARIO ────────────────────────────────────────────────────────
  const abrirNuevo = () => {
    setForm(pedidoVacio());
    setEditingId(null);
    setShowForm(true);
  };
  const abrirEdicion = (p: Pedido) => {
    setForm({
      ...p,
      lineas: p.lineas.map(l => ({
        ...l
      }))
    });
    setEditingId(p.id);
    setShowForm(true);
  };
  const cerrarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(pedidoVacio());
  };

  // Cambiar de formato reinicia el detalle: las líneas de WOP (6
  // compartimientos con producto) y las de GLP (una sola, sin producto) no
  // son intercambiables.
  const cambiarTipo = (tipo: Pedido['tipo']) => {
    setForm(prev => ({
      ...prev,
      tipo,
      lineas: tipo === 'WOP' ? COMPARTIMIENTOS.map(lineaVacia) : [lineaGLPVacia()]
    }));
  };
  const setLinea = (index: number, patch: Partial<PedidoLinea>) => {
    setForm(prev => ({
      ...prev,
      lineas: (prev.lineas ?? []).map((l, i) => i === index ? {
        ...l,
        ...patch
      } : l)
    }));
  };
  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.numeroOrden?.trim()) {
      alert('Indica el número de orden de pedido.');
      return;
    }
    if (!form.fecha) {
      alert('Indica la fecha del pedido.');
      return;
    }
    if (!form.conductor?.trim() || !form.placaCamion?.trim()) {
      alert('Indica el conductor y la placa del camión.');
      return;
    }
    // Solo se guardan las líneas con galones: en WOP es normal dejar
    // compartimientos vacíos, y esos no deben imprimirse.
    const lineasUtiles = (form.lineas ?? []).filter(l => (l.galones || 0) > 0);
    if (lineasUtiles.length === 0) {
      alert('Registra al menos un compartimiento con galones.');
      return;
    }
    const sinDestino = lineasUtiles.some(l => !l.destino.trim());
    if (sinDestino) {
      alert('Cada compartimiento con galones necesita su destino.');
      return;
    }
    const base = {
      ...form,
      lineas: lineasUtiles
    } as Pedido;
    if (editingId) {
      onUpdate({
        ...base,
        id: editingId
      });
      onLog?.(`Orden de pedido editada: ${base.numeroOrden} (${base.tipo})`);
    } else {
      onAdd({
        ...base,
        id: Date.now().toString(),
        estado: 'borrador',
        autorizadoPor: '',
        autorizadoCargo: '',
        autorizadoFecha: '',
        fecha_creacion: hoyISO()
      });
      onLog?.(`Orden de pedido creada: ${base.numeroOrden} (${base.tipo})`);
    }
    cerrarForm();
  };

  // ── AUTORIZACIÓN ──────────────────────────────────────────────────────
  // Autorizar equivale a firmar el formulario: se estampa quién lo hizo y
  // desde ahí el pedido ya no se edita.
  const autorizar = (p: Pedido) => {
    if (!window.confirm(`Vas a autorizar la orden ${p.numeroOrden} por ${fmtGal(totalGalones(p))} galones. Después de autorizarla no se podrá editar. ¿Continuar?`)) return;
    onUpdate({
      ...p,
      estado: 'autorizado',
      autorizadoPor: usuarioNombre,
      autorizadoCargo: usuarioCargo,
      autorizadoFecha: hoyISO()
    });
    onLog?.(`Orden de pedido autorizada: ${p.numeroOrden} por ${usuarioNombre}`);
  };
  const anular = (p: Pedido) => {
    if (!window.confirm(`¿Anular la orden ${p.numeroOrden}?`)) return;
    onUpdate({
      ...p,
      estado: 'anulado'
    });
    onLog?.(`Orden de pedido anulada: ${p.numeroOrden}`);
  };
  const eliminar = (p: Pedido) => {
    onDelete(p.id);
    onLog?.(`Orden de pedido eliminada: ${p.numeroOrden}`);
    setConfirmDelete(null);
  };

  // ── IMPRESIÓN ─────────────────────────────────────────────────────────
  const imprimir = (p: Pedido) => {
    const css = `
      @page { size: letter; margin: 1.6cm 1.8cm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
      body { font-family: 'Arial', sans-serif; font-size: 10.5pt; color: #000; line-height: 1.35; }
      .letterhead { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 6px; }
      .letterhead img { height: 74px; width: auto; }
      .company { flex: 1; text-align: center; }
      .company .name { font-size: 13pt; font-weight: bold; text-transform: uppercase; }
      .company .line { font-size: 9.5pt; }
      .blank { display: inline-block; min-width: 180px; border-bottom: 1px solid #000; }
      .doc-title { text-align: center; font-weight: bold; font-size: 12pt; text-transform: uppercase; margin: 12px 0 18px; }
      .meta { margin-bottom: 4px; }
      .meta strong { text-transform: uppercase; }
      .to { margin: 14px 0 10px; }
      .intro { text-align: justify; margin-bottom: 16px; }
      .truck { margin-bottom: 4px; }
      .truck .gap { display: inline-block; width: 34px; }
      table { width: 100%; border-collapse: collapse; margin: 16px 0 22px; }
      th, td { border: 1px solid #000; padding: 5px 4px; font-size: 8.5pt; text-align: center; vertical-align: middle; }
      th { font-weight: bold; }
      td.dest { text-align: left; font-size: 9pt; padding-left: 6px; }
      td.compart { text-align: left; font-weight: bold; padding-left: 6px; white-space: nowrap; }
      td.num { text-align: right; padding-right: 6px; font-variant-numeric: tabular-nums; }
      .glp-table th { font-size: 9.5pt; }
      .glp-table td { font-size: 11pt; padding: 10px 6px; }
      .auth { margin-top: 34px; }
      .auth .name { font-weight: bold; border-bottom: 1px solid #000; display: inline-block; min-width: 260px; padding-bottom: 2px; }
      .auth .cargo { margin-top: 4px; font-size: 9.5pt; }
      .void { position: fixed; top: 42%; left: 12%; font-size: 62pt; color: rgba(220,38,38,.22); font-weight: bold; transform: rotate(-22deg); letter-spacing: 6px; }
      @media print { .no-print { display: none; } }
      .no-print { position: fixed; top: 14px; right: 14px; }
      .no-print button {
        background: #FF6B01; color: #fff; border: none; padding: 10px 18px;
        border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
        font-family: sans-serif; box-shadow: 0 6px 18px rgba(255,107,1,.35);
      }
    `;
    const campo = (valor: string, ancho = 180) => valor?.trim() ? `<strong>${esc(valor)}</strong>` : `<span class="blank" style="min-width:${ancho}px"></span>`;

    // Tabla WOP: una fila por compartimiento y una columna por producto; los
    // galones caen en la columna del producto elegido.
    const tablaWOP = () => {
      const heads = PRODUCTOS_WOP.map(prod => `<th>${esc(prod.label)}</th>`).join('');
      const filas = COMPARTIMIENTOS.map(n => {
        const linea = p.lineas.find(l => l.compartimiento === n);
        const celdas = PRODUCTOS_WOP.map(prod => linea && linea.producto === prod.id && linea.galones > 0 ? `<td class="num">${fmtGal(linea.galones)}</td>` : '<td></td>').join('');
        return `<tr>
          <td class="compart">Compart. ${n}</td>
          ${celdas}
          <td class="dest">${esc(linea?.destino ?? '')}</td>
          <td class="dest">${esc(linea?.rncDestino ?? '')}</td>
        </tr>`;
      }).join('');
      return `<table>
        <thead>
          <tr><th>COMPART.</th>${heads}<th>DESTINO</th><th>RNC del Destino</th></tr>
        </thead>
        <tbody>${filas}</tbody>
      </table>`;
    };

    // Tabla GLP: una sola línea con el total de galones.
    const tablaGLP = () => {
      const filas = p.lineas.map(l => `<tr>
          <td class="num">${fmtGal(l.galones)}</td>
          <td class="dest">${esc(l.destino)}</td>
          <td class="dest">${esc(l.rncDestino)}</td>
        </tr>`).join('');
      return `<table class="glp-table">
        <thead>
          <tr>
            <th>CANTIDAD DE GALONES GLP</th>
            <th>DESTINO PEDIDO</th>
            <th>RNC DESTINO</th>
          </tr>
        </thead>
        <tbody>${filas}</tbody>
      </table>`;
    };
    const head = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Orden de Pedido ${esc(p.numeroOrden)} - ${esc(p.tipo)}</title>
${styleTag(css)}
</head>`;
    const body = `
<body>
  <div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>
  ${p.estado === 'anulado' ? '<div class="void">ANULADO</div>' : ''}

  <div class="letterhead">
    <img src="${LOGO_DATA_URI}" alt="${esc(EMPRESA.nombre)}" />
    <div class="company">
      <div class="name">${esc(EMPRESA.nombre)}</div>
      <div class="line">RNC# ${campo(EMPRESA.rnc, 140)}</div>
      <div class="line">${esc(EMPRESA.direccion) || '<span class="blank" style="min-width:340px"></span>'}</div>
      <div class="line">Teléfono de Contacto: ${campo(EMPRESA.telefono, 140)}</div>
    </div>
  </div>

  <div class="doc-title">Formulario de Orden de Pedido / Retiro bajo Contrato</div>

  <div class="meta"><strong>Fecha:</strong> ${esc(fmtFecha(p.fecha))}</div>
  <div class="meta"><strong>Orden de Pedido Número:</strong> ${esc(p.numeroOrden)}</div>

  <div class="to">Señores: &nbsp;&nbsp;&nbsp;&nbsp;<strong>${esc(DESTINATARIO)}</strong></div>

  <div class="intro">
    Por la presente autorizamos el despacho de productos, contra nuestro contrato
    vigente, y de acuerdo al detalle que se consigna más abajo en cantidades
    medidas en Galones Americanos (AG):
  </div>

  <div class="truck">
    <strong>CONDUCTOR DEL CAMION:</strong> ${campo(p.conductor, 220)}
    <span class="gap"></span>
    <strong>Pase del Chofer:</strong> ${campo(p.paseChofer, 110)}
  </div>
  <div class="truck">
    <strong>FICHA DE LA COLA:</strong> ${campo(p.fichaCola, 110)}
    <span class="gap"></span>
    <strong>Placa del Camión:</strong> ${campo(p.placaCamion, 140)}
  </div>

  ${p.tipo === 'WOP' ? tablaWOP() : tablaGLP()}

  <div class="auth">
    <strong>AUTORIZADO POR:</strong>
    <span class="name">${esc(p.autorizadoPor)}</span>
    <div class="cargo">${esc(p.autorizadoCargo)}</div>
  </div>

  ${p.observaciones?.trim() ? `<div style="margin-top:20px;font-size:9.5pt"><strong>Observaciones:</strong> ${esc(p.observaciones)}</div>` : ''}
</body>
</html>`;
    openPrintWindow(head + body);
    onLog?.(`Orden de pedido impresa: ${p.numeroOrden}`);
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return <PageShell title="Órdenes de Pedido" sub={`Formulario de orden de pedido / retiro bajo contrato — ${DESTINATARIO}`} action={canEdit ? <button onClick={abrirNuevo} style={primaryButton} data-id="element-705" data-mp="1ljuizen091mf">
            <Plus size={15} data-id="element-706" data-mp="1plfoug14gfn4" /> Nueva orden
          </button> : undefined} data-id="element-704" data-mp="1gs2myol2y3ow">

      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
      gap: 14,
      marginBottom: 26
    }} data-id="element-707" data-mp="1pmqtsj6aztft">

        <StatCard label="Órdenes" value={String(stats.total)} note="registradas" data-id="element-708" data-mp="xu9vqjjtji7c" />
        <StatCard label="Borradores" value={String(stats.borradores)} note="sin autorizar" accent={T.warn} data-id="element-709" data-mp="11uk2vzjckens" />

        <StatCard label="Autorizadas" value={String(stats.autorizados)} note="listas para despacho" accent={T.ok} data-id="element-710" data-mp="16vzpvx534v8t" />

        <StatCard label="Galones del mes" value={fmtGal(stats.galonesMes)} note="AG solicitados" accent={T.info} data-id="element-711" data-mp="2dcl5uyistdz0" />

      </div>

      {/* ── FILTROS ── */}
      <div style={{
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      marginBottom: 20
    }} data-id="element-712" data-mp="1tw8ck91v4qgd">

        <div style={{
        position: 'relative',
        flex: '1 1 220px'
      }} data-id="element-713" data-mp="1oz827lxzd1jg">

          <Search size={15} style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: T.textMuted
        }} data-id="element-714" data-mp="22qd6508dsfzl" />

          <input style={{
          ...inputStyle,
          paddingLeft: 34
        }} placeholder="Buscar por orden, conductor, placa o destino" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-715" data-mp="2cmg8a638gemj" />

        </div>
        <select style={{
        ...inputStyle,
        flex: '0 1 150px'
      }} value={filterTipo} onChange={e => setFilterTipo(e.target.value as typeof filterTipo)} data-id="element-716" data-mp="2lnnwfvc1bz2w">

          <option value="todos" data-id="element-717" data-mp="rb7rqyyw0t05">Todos los formatos</option>
          <option value="GLP" data-id="element-718" data-mp="n8kv8kr9ujwm">GLP</option>
          <option value="WOP" data-id="element-719" data-mp="n9mhlldqmstn">WOP</option>
        </select>
        <select style={{
        ...inputStyle,
        flex: '0 1 160px'
      }} value={filterEstado} onChange={e => setFilterEstado(e.target.value as typeof filterEstado)} data-id="element-720" data-mp="d4mjfa6p4lba">

          <option value="todos" data-id="element-721" data-mp="h8c61u3t8sto">Todos los estados</option>
          <option value="borrador" data-id="element-722" data-mp="3dryqa93ltry">Borrador</option>
          <option value="autorizado" data-id="element-723" data-mp="3d0bpntkh4da">Autorizado</option>
          <option value="anulado" data-id="element-724" data-mp="8cmq40l4sqno">Anulado</option>
        </select>
      </div>

      {/* ── FORMULARIO ── */}
      <AnimatePresence data-id="element-725" data-mp="3pkhttc0itlx2">
        {showForm && <motion.form onSubmit={guardar} initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} style={{
        ...glassLight,
        padding: isMobile ? 18 : 24,
        marginBottom: 24,
        overflow: 'hidden'
      }} data-id="element-726" data-mp="3ul4gaqdw898e">

            <SectionTitle data-id="element-727" data-mp="1evb68etpoiwb">
              {editingId ? 'Editar orden de pedido' : 'Nueva orden de pedido'}
            </SectionTitle>

            <Field label="Formato del formulario" data-id="element-728" data-mp="1jr07cmldq7uq">
              <div style={{
            display: 'flex',
            gap: 10
          }} data-id="element-729" data-mp="2nc7udkfbsqkk">

                {(['GLP', 'WOP'] as const).map(t => {
              const activo = form.tipo === t;
              return <button key={t} type="button" onClick={() => cambiarTipo(t)} style={{
                ...(activo ? primaryButton : ghostButton),
                flex: 1,
                justifyContent: 'center'
              }} data-id="element-730" data-mp="2dg4ihmuo9dkf">

                      {t === 'GLP' ? <Fuel size={15} data-id="element-731" data-mp="361xp67j39cch" /> : <Truck size={15} data-id="element-732" data-mp="3b2lemoro5l4l" />}
                      {t === 'GLP' ? 'GLP' : 'WOP (derivados)'}
                    </button>;
            })}
              </div>
            </Field>

            <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 16
        }} data-id="element-733" data-mp="314nqe19t7obz">

              <Field label="Orden de pedido número *" data-id="element-734" data-mp="1lmu9sb63xsoj">
                <input style={inputStyle} value={form.numeroOrden ?? ''} onChange={e => setForm({
              ...form,
              numeroOrden: e.target.value
            })} placeholder="1587725" data-id="element-735" data-mp="1ppinox9ohpsd" />

              </Field>
              <Field label="Fecha *" data-id="element-736" data-mp="1d7oayvqmiran">
                <input type="date" style={inputStyle} value={form.fecha ?? ''} onChange={e => setForm({
              ...form,
              fecha: e.target.value
            })} data-id="element-737" data-mp="1d6mz4p1sa0rv" />

              </Field>
              <Field label="Conductor del camión *" data-id="element-738" data-mp="1n5ua55l1pjbt">
                <input style={inputStyle} value={form.conductor ?? ''} onChange={e => setForm({
              ...form,
              conductor: e.target.value
            })} placeholder="Nombre completo" data-id="element-739" data-mp="zeykof42b07q" />

              </Field>
              <Field label="Pase del chofer" data-id="element-740" data-mp="196301fxngn33">
                <input style={inputStyle} value={form.paseChofer ?? ''} onChange={e => setForm({
              ...form,
              paseChofer: e.target.value
            })} placeholder="APP-87" data-id="element-741" data-mp="14el2l1uu54mv" />

              </Field>
              <Field label="Ficha de la cola" data-id="element-742" data-mp="bqp7g1kwzks7">
                <input style={inputStyle} value={form.fichaCola ?? ''} onChange={e => setForm({
              ...form,
              fichaCola: e.target.value
            })} placeholder="33-21" data-id="element-743" data-mp="bpnlv369vfw8" />

              </Field>
              <Field label="Placa del camión *" data-id="element-744" data-mp="15da8sznee3ht">
                <input style={inputStyle} value={form.placaCamion ?? ''} onChange={e => setForm({
              ...form,
              placaCamion: e.target.value
            })} placeholder="L-317355" data-id="element-745" data-mp="1a7o4z49vwenj" />

              </Field>
            </div>

            <SectionTitle data-id="element-746" data-mp="ihjpnk0zeht6">
              {form.tipo === 'WOP' ? 'Detalle por compartimiento' : 'Detalle del despacho'}
            </SectionTitle>

            <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginBottom: 18
        }} data-id="element-747" data-mp="retzd0y5hyvz">

              {lineas.map((linea, i) => <div key={i} style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : form.tipo === 'WOP' ? '96px 1.3fr 100px 1.4fr 1fr' : '120px 1.6fr 1fr',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd
          }} data-id="element-748" data-mp="22vagqzt9mplh" data-mpi={i}>

                  {form.tipo === 'WOP' && <span style={{
              fontSize: 12,
              fontWeight: 700,
              color: T.text
            }} data-id="element-749" data-mp="3il1pg4bde0ml">

                      Compart. {linea.compartimiento}
                    </span>}
                  {form.tipo === 'WOP' && <select style={inputStyle} value={linea.producto} onChange={e => setLinea(i, {
              producto: e.target.value as ProductoWOP
            })} data-id="element-750" data-mp="39jbnw5nrxlb3">

                      {PRODUCTOS_WOP.map(prod => <option key={prod.id} value={prod.id} data-id="element-751" data-mp="2zpbkc8854o4h">
                          {prod.label}
                        </option>)}
                    </select>}
                  <input type="number" min={0} style={inputStyle} value={linea.galones || ''} onChange={e => setLinea(i, {
              galones: Number(e.target.value) || 0
            })} placeholder="Galones" data-id="element-752" data-mp="xuu31yik0e6o" />

                  <input style={inputStyle} value={linea.destino} onChange={e => setLinea(i, {
              destino: e.target.value
            })} placeholder="Destino" data-id="element-753" data-mp="10yi2vy5d6vfd" />

                  <input style={inputStyle} value={linea.rncDestino} onChange={e => setLinea(i, {
              rncDestino: e.target.value
            })} placeholder="RNC del destino" data-id="element-754" data-mp="qyiymdfz9r8b" />

                </div>)}
            </div>

            {form.tipo === 'WOP' && <div style={{
          fontSize: 11,
          color: T.textMuted,
          marginBottom: 16,
          marginTop: -8
        }} data-id="element-755" data-mp="mxyyd103y7hy">

                Los compartimientos sin galones no se imprimen en el formulario.
              </div>}

            <Field label="Observaciones" data-id="element-756" data-mp="d8xkbgd4i06m">
              <input style={inputStyle} value={form.observaciones ?? ''} onChange={e => setForm({
            ...form,
            observaciones: e.target.value
          })} placeholder="Opcional" data-id="element-757" data-mp="d5t6rsdrps3a" />

            </Field>

            <div style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end'
        }} data-id="element-758" data-mp="3gqsabp6ntmps">

              <button type="button" onClick={cerrarForm} style={ghostButton} data-id="element-759" data-mp="3bt0lx5584iqc">
                Cancelar
              </button>
              <button type="submit" style={primaryButton} data-id="element-760" data-mp="3ptbcs86j9xbi">
                {editingId ? 'Guardar cambios' : 'Crear orden'}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>

      {/* ── LISTADO ── */}
      {filtrados.length === 0 ? <EmptyState title="Sin órdenes de pedido" hint={canEdit ? 'Crea la primera orden para imprimir el formulario de Refidomsa.' : 'Tu rol no permite crear órdenes de pedido.'} icon={<FileText size={22} data-id="element-762" data-mp="8b8qthvsjinx" />} data-id="element-761" data-mp="3urmnel4nkbus" /> : <>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }} data-id="element-763" data-mp="8cjqb5i7e498">

            {pageItems.map(p => {
          const es = estadoStyle(p.estado);
          const bloqueado = p.estado !== 'borrador';
          return <motion.div key={p.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} style={{
            ...glassLight,
            padding: isMobile ? 16 : '18px 20px',
            display: 'flex',
            gap: 16,
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap'
          }} data-id="element-764" data-mp="ha3wmsuxaqqp">

                  <div style={{
              flex: '1 1 260px',
              minWidth: 0
            }} data-id="element-765" data-mp="3ttwa4r520295">

                    <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                marginBottom: 6
              }} data-id="element-766" data-mp="2ma2yoj4lhnj">

                      <span style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: T.text
                }} data-id="element-767" data-mp="bmf9yxkeqe16">

                        Orden {p.numeroOrden || '—'}
                      </span>
                      <Badge label={p.tipo} color={p.tipo === 'GLP' ? T.brand : T.info} soft={p.tipo === 'GLP' ? T.accentSoft : T.infoSoft} dot={false} data-id="element-768" data-mp="25wwv3mps578c" />

                      <Badge label={es.label} color={es.color} soft={es.soft} data-id="element-769" data-mp="213u2phwqo3tq" />
                    </div>
                    <div style={{
                fontSize: 12,
                color: T.textSecondary
              }} data-id="element-770" data-mp="1w4hccn6nseme">

                      {fmtFecha(p.fecha)} · {p.conductor || 'Sin conductor'} ·{' '}
                      {p.placaCamion || 'Sin placa'}
                    </div>
                    <div style={{
                fontSize: 12,
                color: T.textMuted,
                marginTop: 3
              }} data-id="element-771" data-mp="2jubpl5uw4576">

                      {p.lineas.length}{' '}
                      {p.lineas.length === 1 ? 'destino' : 'destinos'} ·{' '}
                      {fmtGal(totalGalones(p))} galones
                      {p.estado === 'autorizado' && p.autorizadoPor ? ` · Autorizó ${p.autorizadoPor}` : ''}
                    </div>
                  </div>

                  <div style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap'
            }} data-id="element-772" data-mp="2yjrqu3en1f91">

                    <button type="button" onClick={() => imprimir(p)} style={iconBtn} title="Imprimir formulario" data-id="element-773" data-mp="2plejzwtjyzhv">

                      <Printer size={14} data-id="element-774" data-mp="2ukivwv7v3bbw" />
                    </button>

                    {canAutorizar && p.estado === 'borrador' && <button type="button" onClick={() => autorizar(p)} style={{
                ...iconBtn,
                borderColor: `${T.ok}55`,
                color: T.ok
              }} title="Autorizar orden" data-id="element-775" data-mp="3ia3qkbddnx10">

                        <ShieldCheck size={14} data-id="element-776" data-mp="38ho6n8iisydh" />
                      </button>}

                    {canEdit && !bloqueado && <button type="button" onClick={() => abrirEdicion(p)} style={iconBtn} title="Editar" data-id="element-777" data-mp="52sb1e0l2v1m">

                        <Edit2 size={14} data-id="element-778" data-mp="9t8ogy4emgnz" />
                      </button>}

                    {canArchive && p.estado === 'autorizado' && <button type="button" onClick={() => anular(p)} style={{
                ...iconBtn,
                borderColor: `${T.warn}55`,
                color: T.warn
              }} title="Anular orden" data-id="element-779" data-mp="j7iu5cah0ogl">

                        <Ban size={14} data-id="element-780" data-mp="swsnevh1wd6a" />
                      </button>}

                    {canArchive && p.estado !== 'autorizado' && <button type="button" onClick={() => setConfirmDelete(p.id)} style={{
                ...iconBtn,
                borderColor: `${T.danger}55`,
                color: T.danger
              }} title="Eliminar" data-id="element-781" data-mp="nzsqba26ikrb">

                        <Trash2 size={14} data-id="element-782" data-mp="a7m7thbk01op" />
                      </button>}
                  </div>

                  {confirmDelete === p.id && <div style={{
              flex: '1 1 100%',
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
              paddingTop: 12,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-783" data-mp="589o35fzbaqc">

                      <span style={{
                fontSize: 12,
                color: T.textSecondary,
                flex: 1
              }} data-id="element-784" data-mp="35o4r6ybbevlv">

                        ¿Eliminar la orden {p.numeroOrden}? Esta acción no se
                        puede deshacer.
                      </span>
                      <button type="button" onClick={() => setConfirmDelete(null)} style={ghostButton} data-id="element-785" data-mp="2rove47ieohm4">

                        Cancelar
                      </button>
                      <button type="button" onClick={() => eliminar(p)} style={dangerButton} data-id="element-786" data-mp="3ogywf0j4z9cw">

                        Eliminar
                      </button>
                    </div>}
                </motion.div>;
        })}
          </div>

          <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="órdenes" data-id="element-787" data-mp="3anzmde24i7cq" />

        </>}
    </PageShell>;
}